/**
 * Stream selector section for level hub pages (primary + secondary).
 *
 * What: Renders a card grid for choosing a level/stream option on hub pages.
 * Why: Keeps Primary and Secondary hub pages aligned to the same LevelPageLayout structure.
 * When: Update when stream card design, grid breakpoints, or routing changes.
 */
import { Link as RouterLink } from 'react-router-dom';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Typography } from '@mui/material';

import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';

interface StreamOption {
  id: string;
  label: string;
  /**
   * Route to another page (`/sec-3`), or an in-page anchor (`#secondary-3-syllabus`)
   * on hubs whose content sits further down the same page.
   */
  path: string;
  description: string;
}

/**
 * Anchors must render as a plain `<a href="#...">`. A react-router `Link` treats
 * `#foo` as a route, so the card would navigate instead of scrolling.
 */
function isAnchorPath(path: string): boolean {
  return path.startsWith('#');
}

export interface StreamSelectorContent {
  title: string;
  streams: StreamOption[];
}

interface StreamSelectorSectionProps {
  content: StreamSelectorContent;
}

function StreamSelectorSection({ content }: StreamSelectorSectionProps) {
  const titleId = 'stream-selector-title';

  if (!content?.streams?.length) return null;

  // Primary hub has 6 options; render as 3 + 3 on medium+ screens.
  // Other hubs (e.g. secondary) use fewer options and keep the existing 4-column feel.
  const isPrimarySixStreamHub =
    content.streams.length === 6 &&
    content.streams.every(stream => stream.id.startsWith('primary-'));
  const mdFlexBasis = isPrimarySixStreamHub
    ? 'calc(33.333% - 18px)'
    : 'calc(25% - 18px)';

  return (
    <LandingSectionShell
      id="streams"
      ariaLabelledby={titleId}
      // Spacing to the next section comes from the page-level stack.
      mb={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <LandingCenteredSectionHead titleId={titleId} title={content.title} />
      {/* Flex layout avoids MUI Grid negative margins and horizontal overflow on mobile */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          gap: 3,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          '& > *': {
            flex: {
              xs: 'none',
              sm: '1 1 calc(50% - 12px)',
              md: `1 1 ${mdFlexBasis}`,
            },
            minWidth: 0,
            maxWidth: { xs: '100%', sm: 'none', md: 'none' },
          },
        }}
      >
        {content.streams.map(stream => (
          <Card
            key={stream.id}
            {...(isAnchorPath(stream.path)
              ? { component: 'a' as const, href: stream.path }
              : { component: RouterLink, to: stream.path })}
            sx={{
              width: '100%',
              minWidth: 0,
              maxWidth: { xs: '100%', sm: 'none', md: 'none' },
              boxSizing: 'border-box',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              textDecoration: 'none',
              borderRadius: radii.lg,
              border: `2px solid ${colors.border}`,
              backgroundColor: colors.paper,
              boxShadow: shadows.card,
              transition: 'all 0.25s ease',
              '&:hover': {
                borderColor: colors.accentOrange,
                transform: 'translateY(-2px)',
                boxShadow: shadows.lift,
                '& .arrow-icon': {
                  transform: 'translateX(4px)',
                },
              },
            }}
          >
            <CardContent
              sx={{
                p: 3,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  minWidth: 0,
                  gap: 1,
                }}
              >
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 800,
                    color: colors.ink,
                    letterSpacing: -0.3,
                    minWidth: 0,
                    overflowWrap: 'break-word',
                  }}
                >
                  {stream.label}
                </Typography>
                <ArrowIcon
                  className="arrow-icon"
                  sx={{
                    color: colors.accentOrange,
                    fontSize: 24,
                    transition: 'transform 0.2s ease',
                    flexShrink: 0,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: '0.9375rem',
                  lineHeight: 1.5,
                  color: colors.inkMuted,
                  flex: 1,
                  minWidth: 0,
                  overflowWrap: 'break-word',
                }}
              >
                {stream.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </LandingSectionShell>
  );
}

export default StreamSelectorSection;
